import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Alert, Button, Empty, Popconfirm, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    AddAdditionalClassificationModal,
    AdditionalClassificationsActionsRenderer
} from '..';
import { ApplicationDefinitionConditionalEditStore } from '../../stores';
import { AdditionalClassificationDto } from '../../types';
import { AdditionalClassificationDraggableRow } from './AdditionalClassificationDraggableRow';
import './AdditionalClassificationsRenderer.less';

interface Props {
    store: ApplicationDefinitionConditionalEditStore;
    canEdit: boolean;
}

interface AdditionalClassificationTableRow extends AdditionalClassificationDto {
    tableKey: string;
    isRoot: boolean;
    rootIndex?: number;
    children: AdditionalClassificationTableRow[];
}

const tableComponents = {
    body: {
        row: AdditionalClassificationDraggableRow
    }
};

const AdditionalClassificationsRenderer: React.FC<Props> = ({ store, canEdit }) => {
    const countIncludedFields = (classification: AdditionalClassificationDto): number =>
        1 + (classification.children ?? []).reduce((count, child) => count + countIncludedFields(child), 0);

    const getStructure = (classification: AdditionalClassificationTableRow): string => {
        if (!classification.isRoot) {
            return '';
        }

        return classification.children.length > 0 ? 'Has dependent classification' : 'Standalone';
    };

    const flattenIncludedFields = (classification: AdditionalClassificationDto): AdditionalClassificationDto[] => [
        classification,
        ...(classification.children ?? []).reduce<AdditionalClassificationDto[]>(
            (fields, child) => [...fields, ...flattenIncludedFields(child)],
            []
        )
    ];

    const renderDeleteConfirmation = (classification: AdditionalClassificationTableRow) => {
        const fields = flattenIncludedFields(classification);

        return (
            <div className="additional-classifications-renderer__delete-confirmation">
                <div>Delete this classification and the included fields?</div>
                <ul>
                    {fields.map(field => (
                        <li key={`${field.typeCode}-${field.classificationSlot}`}>
                            <span className="additional-classifications-renderer__delete-confirmation-name">
                                {field.displayName} ({field.classificationSlot})
                            </span>
                            <span className="additional-classifications-renderer__delete-confirmation-slot">
                                {field.systemName}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const toTableRows = (
        classifications: AdditionalClassificationDto[],
        parentKey?: string
    ): AdditionalClassificationTableRow[] =>
        classifications.map((classification, index) => {
            const tableKey = parentKey
                ? `${parentKey}/${classification.classificationSlot}`
                : classification.classificationSlot;
            const isRoot = parentKey === undefined;

            return {
                ...classification,
                tableKey,
                isRoot,
                rootIndex: isRoot ? index : undefined,
                children: toTableRows(classification.children ?? [], tableKey)
            };
        });

    const getExpandedRowKeys = (rows: AdditionalClassificationTableRow[]): string[] =>
        rows.reduce<string[]>(
            (keys, row) => [...keys, row.tableKey, ...getExpandedRowKeys(row.children)],
            []
        );

    const tableRows = toTableRows(store.additionalClassifications);
    const moveRow = (dragIndex: number, dropIndex: number) => {
        store.moveAdditionalClassificationToIndex(dragIndex, dropIndex);
    };

    const columns: ColumnsType<AdditionalClassificationTableRow> = [
        {
            title: 'Order',
            key: 'order',
            width: 96,
            render: (_value, record) =>
                record.isRoot ? (
                    <div className="additional-classifications-renderer__order">
                        {canEdit ? (
                            <Tooltip title="Drag to reorder">
                                <span
                                    aria-label={`Drag ${record.displayName}`}
                                    className="additional-classifications-renderer__drag-handle"
                                >
                                    <i className="alpha-icon sm drag-handle" />
                                </span>
                            </Tooltip>
                        ) : null}
                        <span aria-label={`Order ${record.rootIndex! + 1}`}>{record.rootIndex! + 1}</span>
                    </div>
                ) : null
        },
        {
            title: 'Classification',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (_value, record) => (
                <div className="additional-classifications-renderer__classification">
                    <div className="group-title">{record.displayName}</div>
                    <div className="group-subtitle">{record.systemName}</div>
                </div>
            )
        },
        {
            title: 'SICS Slot',
            dataIndex: 'classificationSlot',
            key: 'classificationSlot',
            width: 170
        },
        {
            title: 'Structure',
            key: 'structure',
            width: 210,
            render: (_value, record) => getStructure(record)
        },
        {
            title: 'Included fields',
            key: 'includedFields',
            width: 140,
            render: (_value, record) => countIncludedFields(record)
        }
    ];

    if (canEdit) {
        columns.push({
            title: 'Actions',
            key: 'actions',
            width: 72,
            render: (_value, record) =>
                record.isRoot ? (
                    <div className="row-actions-wrapper additional-classifications-renderer__actions">
                        <Popconfirm
                            title={renderDeleteConfirmation(record)}
                            onConfirm={() => store.removeAdditionalClassification(record)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip placement="top" title="Delete classification">
                                <Button
                                    aria-label={`Remove ${record.displayName}`}
                                    data-id={`additional-classification-delete-${record.classificationSlot}`}
                                    type="link"
                                    size="small"
                                >
                                    <i className="alpha-icon md delete-icon-red" />
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                    </div>
                ) : null
        });
    }

    return (
        <div className="additional-classifications-renderer" data-id="additional-classifications-renderer">
            {canEdit ? <AddAdditionalClassificationModal store={store} /> : null}

            <AdditionalClassificationsActionsRenderer store={store} canEdit={canEdit} />

            {store.additionalClassifications.some(classification => classification.isAvailable === false) ? (
                <Alert
                    type="warning"
                    showIcon
                    message="Some configured classifications are no longer available in SICS."
                    style={{ marginBottom: 16 }}
                />
            ) : null}

            <DndProvider backend={HTML5Backend}>
                <Table<AdditionalClassificationTableRow>
                    rowKey="tableKey"
                    className="alpha-portal-table additional-classifications-renderer__table"
                    columns={columns}
                    dataSource={tableRows}
                    pagination={false}
                    components={canEdit && store.additionalClassifications.length > 1 ? tableComponents : undefined}
                    onRow={
                        canEdit && store.additionalClassifications.length > 1
                            ? record =>
                                  ({
                                      index: record.rootIndex,
                                      record,
                                      moveRow
                                  }) as React.HTMLAttributes<HTMLElement>
                            : undefined
                    }
                    expandable={{
                        expandedRowKeys: getExpandedRowKeys(tableRows),
                        expandIcon: () => null,
                        indentSize: 20
                    }}
                    locale={{
                        emptyText: <Empty description="No additional classifications configured" />
                    }}
                />
            </DndProvider>
        </div>
    );
};

export default observer(AdditionalClassificationsRenderer);
